/**
 * API Client for 100metric SSP
 *
 * Axios instance with interceptors for authentication and error handling.
 */
import axios, { type AxiosInstance, type AxiosError, type InternalAxiosRequestConfig } from 'axios'
import type { ApiError } from '@/types'
import { FIELD_NAMES } from '@/utils/error'

// Store active abort controllers by request key
const activeRequests = new Map<string, AbortController>()

/**
 * Create an abortable request with automatic cleanup of previous requests with same key
 * @param key - Unique identifier for the request
 * @returns Object with abort signal and cleanup function
 */
export const createAbortableRequest = (key: string) => {
  // Cancel previous request with same key
  if (activeRequests.has(key)) {
    activeRequests.get(key)?.abort()
  }

  const controller = new AbortController()
  activeRequests.set(key, controller)

  const cleanup = () => activeRequests.delete(key)

  return {
    signal: controller.signal,
    cleanup,
    // Auto-cleanup when signal fires
    withAutoCleanup: <T,>(promise: Promise<T>): Promise<T> => {
      return promise.finally(cleanup)
    },
  }
}

/**
 * Cancel all pending requests
 */
export const cancelAllRequests = () => {
  activeRequests.forEach((controller) => {
    controller.abort()
  })
  activeRequests.clear()
}

// Store pending requests to deduplicate identical requests in flight
const pendingRequests = new Map<string, Promise<any>>()

/**
 * Deduplicate requests by returning same promise for duplicate in-flight requests
 * @param key - Unique identifier for the request
 * @param fn - Function that returns the request promise
 * @returns Promise that resolves/rejects with request result
 */
export const deduplicateRequest = (
  key: string,
  fn: () => Promise<any>
): Promise<any> => {
  // Return existing promise if request in flight
  if (pendingRequests.has(key)) {
    return pendingRequests.get(key)!
  }

  // Create new promise and store it
  const promise = fn()

  // Attach cleanup handlers - clear cache on both success and error
  promise
    .then(
      (result) => {
        pendingRequests.delete(key)
        return result
      },
      (error) => {
        pendingRequests.delete(key)
        throw error
      }
    )
    .catch(() => {
      // Prevent unhandled rejection warning for stored promise
    })

  pendingRequests.set(key, promise)
  return promise
}

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: '/api/v1',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Token storage keys
const ACCESS_TOKEN_KEY = 'access_token'
const REFRESH_TOKEN_KEY = 'refresh_token'

// Token management
export const tokenStorage = {
  getAccessToken: (): string | null => localStorage.getItem(ACCESS_TOKEN_KEY),
  getRefreshToken: (): string | null => localStorage.getItem(REFRESH_TOKEN_KEY),

  setTokens: (accessToken: string, refreshToken: string): void => {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken)
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
  },

  clearTokens: (): void => {
    localStorage.removeItem(ACCESS_TOKEN_KEY)
    localStorage.removeItem(REFRESH_TOKEN_KEY)
  },
}

const IMPERSONATION_TOKEN_KEY = 'impersonation_token'

export const impersonationStorage = {
  getToken: (): string | null => sessionStorage.getItem(IMPERSONATION_TOKEN_KEY),
  clear: (): void => sessionStorage.removeItem(IMPERSONATION_TOKEN_KEY),
  isActive: (): boolean => !!sessionStorage.getItem(IMPERSONATION_TOKEN_KEY),
}

// Request interceptor - add auth token and fix FormData Content-Type
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = impersonationStorage.getToken() ?? tokenStorage.getAccessToken()
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }
    // When sending FormData, clear the default application/json Content-Type so
    // axios doesn't convert FormData to JSON (axios 1.x converts FormData to JSON
    // when Content-Type is application/json, stripping binary file content).
    // Clearing it lets the browser set multipart/form-data with the correct boundary.
    if (config.data instanceof FormData) {
      config.headers['Content-Type'] = undefined
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor - handle errors and token refresh
let isRefreshing = false
let refreshPromise: Promise<string> | null = null

/**
 * Refresh access token with mutex pattern
 *
 * If a refresh is already in progress, returns the cached promise
 * to ensure only one refresh request is made for concurrent 401 errors.
 */
const refreshAccessToken = async (): Promise<string> => {
  // If already refreshing, wait for that refresh to complete
  if (isRefreshing && refreshPromise) {
    return refreshPromise
  }

  isRefreshing = true

  refreshPromise = (async () => {
    try {
      const refreshToken = tokenStorage.getRefreshToken()
      if (!refreshToken) {
        throw new Error('No refresh token available')
      }

      const response = await apiClient.post('/auth/refresh', {
        refresh_token: refreshToken,
      })

      const { access_token, refresh_token: new_refresh_token } = response.data
      tokenStorage.setTokens(access_token, new_refresh_token)

      return access_token
    } finally {
      isRefreshing = false
      refreshPromise = null
    }
  })()

  return refreshPromise
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiError>) => {
    const originalRequest = error.config

    const extractMessage = (detail: unknown, fallback: string): string => {
      if (typeof detail === 'string' && detail.trim()) return detail
      if (Array.isArray(detail)) {
        const parts = detail
          .map((item) => {
            if (typeof item === 'string') return item
            if (item && typeof item === 'object' && 'msg' in item) {
              const msg = (item as { msg?: unknown }).msg
              if (typeof msg === 'string') {
                const loc = (item as { loc?: unknown }).loc
                if (Array.isArray(loc) && loc.length > 0) {
                  const fieldKey = String(loc[loc.length - 1])
                  const fieldName = FIELD_NAMES[fieldKey]
                  if (fieldName) {
                    const msgStr = String(msg)
                    return `${fieldName}: ${msgStr.charAt(0).toLowerCase() + msgStr.slice(1)}`
                  }
                }
                return msg
              }
            }
            try {
              return JSON.stringify(item)
            } catch {
              return String(item)
            }
          })
          .filter(Boolean)
        return parts.length ? parts.join('; ') : fallback
      }
      if (detail && typeof detail === 'object') {
        if ('message' in detail && typeof (detail as { message?: unknown }).message === 'string') {
          return (detail as { message: string }).message
        }
        try {
          return JSON.stringify(detail)
        } catch {
          return fallback
        }
      }
      return fallback
    }

    const translateMessage = (message: string): string => {
      const text = message.trim()
      if (!text) return 'Произошла ошибка'

      if (text === 'Network Error') {
        return 'Ошибка сети. Проверьте подключение и доступность сервера.'
      }

      if (text === 'Request failed with status code 401') {
        return 'Неавторизовано'
      }

      const httpCodeMatch = text.match(/^Request failed with status code (\d+)\.?$/i)
      if (httpCodeMatch) {
        const code = parseInt(httpCodeMatch[1], 10)
        if (code === 403) return 'Доступ запрещён'
        if (code === 404) return 'Ресурс не найден'
        if (code >= 500) return `Ошибка сервера (${code})`
        return `Ошибка запроса (${code})`
      }

      const internalMap: Array<[RegExp, (m: RegExpMatchArray) => string]> = [
        [/^Слишком много запросов\./i, () => 'Превышен лимит запросов. Попробуйте позже.'],
        [/^Rate limit/i, () => 'Превышен лимит запросов'],
        [/^Login failed$/i, () => 'Ошибка входа'],
        [/^Update failed$/i, () => 'Не удалось обновить данные'],
        [/^Failed to fetch products$/i, () => 'Не удалось загрузить товары'],
        [/^Failed to fetch product$/i, () => 'Не удалось загрузить товар'],
        [/^Failed to create product$/i, () => 'Не удалось создать товар'],
        [/^Failed to update product$/i, () => 'Не удалось обновить товар'],
        [/^Failed to delete product$/i, () => 'Не удалось удалить товар'],
        [/^Failed to export products$/i, () => 'Не удалось экспортировать товары'],
        [/^Failed to import products$/i, () => 'Не удалось импортировать товары'],
        [/^Failed to fetch categories$/i, () => 'Не удалось загрузить категории'],
        [/^Failed to create category$/i, () => 'Не удалось создать категорию'],
        [/^Failed to update category$/i, () => 'Не удалось обновить категорию'],
        [/^Failed to delete category$/i, () => 'Не удалось удалить категорию'],
        [/^Failed to fetch suppliers$/i, () => 'Не удалось загрузить поставщиков'],
        [/^Failed to create supplier$/i, () => 'Не удалось создать поставщика'],
        [/^Failed to update supplier$/i, () => 'Не удалось обновить поставщика'],
        [/^Failed to delete supplier$/i, () => 'Не удалось удалить поставщика'],
        [/^Failed to fetch legal entities$/i, () => 'Не удалось загрузить юридические лица'],
        [/^Failed to create legal entity$/i, () => 'Не удалось создать юридическое лицо'],
        [/^Failed to update legal entity$/i, () => 'Не удалось обновить юридическое лицо'],
        [/^Failed to delete legal entity$/i, () => 'Не удалось удалить юридическое лицо'],
        [/^Failed to fetch cabinets$/i, () => 'Не удалось загрузить кабинеты'],
        [/^Failed to fetch cabinet$/i, () => 'Не удалось загрузить кабинет'],
        [/^Failed to create cabinet$/i, () => 'Не удалось создать кабинет'],
        [/^Failed to update cabinet$/i, () => 'Не удалось обновить кабинет'],
        [/^Failed to delete cabinet$/i, () => 'Не удалось удалить кабинет'],
        [/^Failed to fetch companies$/i, () => 'Не удалось загрузить компании'],
        [/^Failed to create company$/i, () => 'Не удалось создать компанию'],
        [/^Failed to update company$/i, () => 'Не удалось обновить компанию'],
        [/^Failed to fetch users$/i, () => 'Не удалось загрузить пользователей'],
        [/^Failed to create user$/i, () => 'Не удалось создать пользователя'],
        [/^Failed to update user$/i, () => 'Не удалось обновить пользователя'],
        [/^Failed to delete user$/i, () => 'Не удалось удалить пользователя'],
        [/^Failed to fetch product groups$/i, () => 'Не удалось загрузить группы товаров'],
        [/^Failed to create product group$/i, () => 'Не удалось создать группу товаров'],
        [/^Failed to update product group$/i, () => 'Не удалось обновить группу товаров'],
        [/^Failed to delete product group$/i, () => 'Не удалось удалить группу товаров'],
        [/^Failed to export$/i, () => 'Не удалось выполнить экспорт'],
        [/^Failed to import$/i, () => 'Не удалось выполнить импорт'],
        [/^Failed to fetch planning grid$/i, () => 'Не удалось загрузить данные планирования'],
        [/^Failed to fetch budget entries$/i, () => 'Не удалось загрузить расходы'],
        [/^Failed to create budget entry$/i, () => 'Не удалось создать расход'],
        [/^Failed to update budget entry$/i, () => 'Не удалось обновить расход'],
        [/^Failed to delete budget entry$/i, () => 'Не удалось удалить расход'],
        [/^Failed to bulk update budget entries$/i, () => 'Не удалось массово обновить расходы'],
        [/^Failed to bulk upsert budget entries$/i, () => 'Не удалось массово сохранить расходы'],
        [/^Failed to copy budget entries$/i, () => 'Не удалось скопировать расходы'],
        [/^Failed to fetch loans$/i, () => 'Не удалось загрузить кредиты'],
        [/^Failed to create loan$/i, () => 'Не удалось создать кредит'],
        [/^Failed to update loan$/i, () => 'Не удалось обновить кредит'],
        [/^Failed to delete loan$/i, () => 'Не удалось удалить кредит'],
        [/^Failed to fetch Procurement report$/i, () => 'Не удалось загрузить отчёт по закупкам'],
        [/^Failed to fetch P&L report$/i, () => 'Не удалось загрузить отчёт P&L'],
        [/^Failed to fetch Cash Flow report$/i, () => 'Не удалось загрузить отчёт Cash Flow'],
        [/^Failed to fetch Balance Sheet report$/i, () => 'Не удалось загрузить отчёт Balance Sheet'],
        [/^Failed to fetch simplified balance$/i, () => 'Не удалось загрузить упрощённый баланс'],
        [/^Failed to recalculate financials$/i, () => 'Не удалось пересчитать финансы'],
      ]

      for (const [re, toRu] of internalMap) {
        const match = text.match(re)
        if (match) return toRu(match)
      }

      const pydanticV2Map: Array<[RegExp, (m: RegExpMatchArray) => string]> = [
        [/^Input should be less than or equal to (\d+)\.?$/i, (m) => `Значение должно быть меньше или равно ${m[1]}`],
        [/^Input should be greater than or equal to (\d+)\.?$/i, (m) => `Значение должно быть больше или равно ${m[1]}`],
        [/^Input should be a valid email address\.?$/i, () => 'Введите корректный email'],
        [/^Field required\.?$/i, () => 'Поле обязательно'],
        [/^String should have at least (\d+) characters\.?$/i, (m) => `Строка должна содержать минимум ${m[1]} символов`],
        [/^String should have at most (\d+) characters\.?$/i, (m) => `Строка должна содержать максимум ${m[1]} символов`],
      ]

      for (const [re, toRu] of pydanticV2Map) {
        const match = text.match(re)
        if (match) return toRu(match)
      }

      const pydanticV1Map: Array<[RegExp, (m: RegExpMatchArray) => string]> = [
        [/^ensure this value is less than or equal to (\d+)\.?$/i, (m) => `Значение должно быть меньше или равно ${m[1]}`],
        [/^ensure this value is greater than or equal to (\d+)\.?$/i, (m) => `Значение должно быть больше или равно ${m[1]}`],
        [/^field required\.?$/i, () => 'Поле обязательно'],
        [/^value is not a valid email address\.?$/i, () => 'Введите корректный email'],
        [/^ensure this value has at least (\d+) characters\.?$/i, (m) => `Строка должна содержать минимум ${m[1]} символов`],
        [/^ensure this value has at most (\d+) characters\.?$/i, (m) => `Строка должна содержать максимум ${m[1]} символов`],
      ]

      for (const [re, toRu] of pydanticV1Map) {
        const match = text.match(re)
        if (match) return toRu(match)
      }

      return text
    }

    // Handle 429 errors (rate limit exceeded)
    if (error.response?.status === 429) {
      const retryAfter = error.response.headers['retry-after']
      let message = 'Слишком много запросов. Попробуйте позже.'
      if (retryAfter) {
        const seconds = parseInt(retryAfter, 10)
        if (!isNaN(seconds)) {
          message = `Слишком много запросов. Повторите попытку через ${seconds} сек.`
        }
      }
      const httpError = new Error(message) as Error & { status?: number; data?: unknown }
      httpError.status = 429
      httpError.data = error.response?.data
      return Promise.reject(httpError)
    }

    // Handle 401 errors (unauthorized)
    if (error.response?.status === 401 && originalRequest) {
      // Impersonation session expired — do NOT run the refresh flow.
      // The localStorage refresh token belongs to the superadmin's own session;
      // silently refreshing it would switch identity mid-request.
      if (impersonationStorage.isActive()) {
        impersonationStorage.clear()
        window.dispatchEvent(new CustomEvent('impersonation-expired'))
        return Promise.reject(error)
      }

      const url = originalRequest.url ?? ''
      const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/register')

      // Don't retry if it's the refresh endpoint itself
      if (url.includes('/auth/refresh')) {
        tokenStorage.clearTokens()
        window.location.href = '/login'
        return Promise.reject(error)
      }

      const refreshToken = tokenStorage.getRefreshToken()
      const accessToken = tokenStorage.getAccessToken()

      if (!refreshToken) {
        tokenStorage.clearTokens()
        if (accessToken && !isAuthEndpoint) {
          window.location.href = '/login'
          return Promise.reject(error)
        }
      } else if (!isAuthEndpoint) {
        // Use mutex pattern: only one refresh happens, others wait for it
        try {
          const newToken = await refreshAccessToken()

          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`
          }

          return apiClient(originalRequest)
        } catch (refreshError) {
          tokenStorage.clearTokens()
          window.location.href = '/login'
          return Promise.reject(refreshError)
        }
      }
    }

    // Extract error message
    let message = extractMessage(
      error.response?.data?.detail,
      error.message || 'Произошла ошибка'
    )

    if (error.response?.status === 401 && originalRequest?.url?.includes('/auth/login')) {
      if (message.includes('status code 401')) {
        message = 'Неверный email или пароль'
      }
    }

    message = translateMessage(message)

    const httpError = new Error(message) as Error & { status?: number; data?: unknown }
    httpError.status = error.response?.status
    httpError.data = error.response?.data

    return Promise.reject(httpError)
  }
)

export default apiClient
